<script setup>
import { register, confirmEmail } from "@/services/ApiUrlService"
import { useRoute } from "vue-router"
import { useApiHandler } from "@/composables/usePostApi"
import { router } from "@/plugins/1.router"
import { useAuthStore } from "@/stores/authStore"
import { useCustomSwal } from "@/composables/usePopup"
import { codePhone } from "@/utils/code"

definePage({
  meta: {
    layout: "new-home",
    unauthenticatedOnly: true,
  },
})
const { showCustomSwal } = useCustomSwal()
const CURRENT_YEAR = import.meta.env.VITE_CURRENT_YEAR || 2025
const authStore = useAuthStore()
const { handleApiRequest, isLoading, error } = useApiHandler()
const route = useRoute()
const isEmail = !!route.query.email
const isToken = !!route.query.token

const text = ref(null)

const itemsVous = [
  {
    id: 1,
    name: $gettext("Representative of a higher education institution / program"),
  },
  { id: 2, name: $gettext("A recruiter / HR professional") },
  { id: 3, name: $gettext("A student / program participant") },
]

const createPassword = async (obj) => {
  const response = await handleApiRequest(confirmEmail, obj, {
    loading: true,
    noPushMessage: true,
  })

  if (response) {
    if (response.result === "invalid token") {
      await showCustomSwal({
        html: `<h4 class="text-error mb-0">${$gettext("Invalid or expired token"
        )}</h4>`,
        confirmButtonColor: "#716296",
        confirmButtonText: $gettext("Cancel"),
      })
      return
    }
    await showCustomSwal({
      html: `<h4 class="text-success mb-0">${$gettext("Your new password has been successfully saved."
      )}</h4>`,
      showCancelButton: false,
      showConfirmButton: false,
      allowOutsideClick: false,
      timer: 5000,
      timerProgressBar: true,
      willClose: () => {
        authStore.loginByCreatePassword(response)
      },
    })
  } else {
    await showCustomSwal({
      html: `<h4 class="text-error">${$gettext("A system error has occurred."
      )}</h4>`,
      showCancelButton: true,
      showConfirmButton: false,
      cancelButtonColor: "#716296",
      cancelButtonText: $gettext("Cancel"),
    })
  }
}

let timeValid = null
const registerUser = async (obj) => {
  if (obj.telephoneCode) {
    obj.telephoneCode = obj.telephoneCode.code
  }
  if (obj.mobileCode) {
    obj.mobileCode = obj.mobileCode.code
  }
  const response = await handleApiRequest(register, obj, {
    loading: true,
    noPushMessage: true,
  })

  if (response) {
    await showCustomSwal({
      // title: $gettext("Votre inscription a bien été enregistrée !"),
      html: `<h3 class="text-primary mb-3 text-center">${$gettext(
        "Thank you! Your registration has been successfully recorded."
      )}</h3> <p class="text-black text-center mb-0">${$gettext(
        "You will receive an email within 48 hours containing your login credentials. Please check your spam or junk folder if you do not see it in your inbox."
      )}</p>
    
    
    `,
      showCancelButton: false,
      showConfirmButton: false,
      showCloseButton: true,
      allowOutsideClick: false,
      timer: 5000,
      timerProgressBar: true,
      willClose: () => {
        redirectLogin()
      },
    })
  } else {
    console.log(error.value)
    if (error.value === "Email already exists") {
      await showCustomSwal({
        html: `<p class="text-error mb-0">${$gettext("The email already exists"
        )}</p>`,
        showCancelButton: false,
        showConfirmButton: false,
        showCloseButton: true,
      })
    } else {
      await showCustomSwal({
        html: `<p class="text-error mb-0">${$gettext("A system error has occurred."
        )}</p>`,
        showCancelButton: false,
        showConfirmButton: false,
        showCloseButton: true,
      })
    }
  }
}

const onSubmit = async (obj) => {
  if (isEmail && isToken) {
    createPassword(obj)
  } else {
    registerUser(obj)
  }
}

const redirectLogin = () => {
  router.push({ name: "index", query: { openLogin: "1" } })
}
</script>

<template>
  <div class="register">
    <section
      id="login"
      class="hero mb-4"
    >
      <div class="hero__overlay">
        <div class="home-page__container hero__content">
          <h1>Eduniversal Programs<br class="d-md-none d-block"> Survey {{ +CURRENT_YEAR }}</h1>
          <h2>
            {{ +CURRENT_YEAR + 1 }} Eduniversal Ranking of the <br class="d-md-none d-block"> Best Masters, MBA & DBA <br>
          </h2>
        </div>
      </div>
    </section>

    <div class="register__form" v-if="!isEmail && !isToken">
      <FormUser
        :listVous="itemsVous"
        :listTele="codePhone"
        :loading="isLoading"
        @update:modelValue="onSubmit($event)"
      />
    </div>

    <div class="register__license" v-if="!isEmail && !isToken">
      <p>
        {{ $gettext("By validating this information, you accept our") }}
        <a
          href="https://www.eduniversal-eea.com/politique-de-confidentialite.html?language=en"
          target="_blank"
        >
          {{ $gettext("privacy policy") }}
        </a>
      </p>
      <p>
        {{ $gettext("You already have an account") }} ?
        <span @click="redirectLogin">{{ $gettext("Log in") }}</span>
      </p>
    </div>
    <div class="register__form" v-if="isEmail && isToken">
      <FormPassword
        :loading="isLoading"
        :text="text"
        @update:modelValue="onSubmit($event)"
      />
    </div>
  </div>
</template>

<style lang="scss">
@import "@/assets/styles/pages/register.scss";
</style>
